import { HqoClientSdk, SdkEvents } from '@hqo/hqo-miniapp-client-sdk';
import { useMiniappSdkEventHandler } from 'app/shared/hooks/use-miniapp-sdk/use-miniapp-sdk.hooks';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';
import { AccordionContent, AccordionItem, AccordionTrigger, Button } from '@hqo/ui-atoms';
import { FormattedMessage } from 'react-intl';
import React, { useCallback, useState } from 'react';

type RootActions = keyof Pick<HqoClientSdk, 'refreshToken' | 'redirectToContent'>;

interface MiniappSdkRootMethodDemoProps {
  action: RootActions;
  eventName: SdkEvents;
  children?: React.ReactNode;
  args?: unknown[];
}

export const MiniappSdkRootMethodDemo = ({
  action,
  eventName,
  children,
  args = [],
}: MiniappSdkRootMethodDemoProps): JSX.Element => {
  const [eventData, setEventData] = useState<string | null>(null);
  const client = useMiniappSdk();

  useMiniappSdkEventHandler(
    eventName,
    (data: unknown) => {
      setEventData(JSON.stringify(data, null, 2));
    },
    [setEventData],
  );

  const handleClick = useCallback(() => {
    switch (action) {
    case 'refreshToken':
      client.refreshToken();
      break;
    case 'redirectToContent':
      client?.redirectToContent(args[0] as Parameters<HqoClientSdk['redirectToContent']>[0]);
      break;
    default:
      break;
    }
  }, [action, args, client]);

  return (
    <AccordionItem value={action} className="rounded-lg border bg-background px-4 last:border-b">
      <AccordionTrigger className="font-mono text-sm">{action}</AccordionTrigger>
      <AccordionContent className="flex flex-col items-start gap-3 pb-4">
        {children}
        <Button onClick={handleClick} variant="default" size="sm" data-testid={`run-${action}`}>
          <FormattedMessage id="buttons.run" />
        </Button>
        {eventData && (
          <pre className="w-full overflow-x-auto rounded-md bg-muted p-3 text-xs">{eventData}</pre>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};
